import { useState, useEffect } from 'react'
import { Users, MessageCircle, Wifi, User } from 'lucide-react'
import { useP2PStore } from '../../store/p2pStore'

export function HomePage() {
  const [currentTime, setCurrentTime] = useState(new Date())
  
  const { 
    connections, 
    connectionManager, 
    isNetworkActive,
    currentSelfta
  } = useP2PStore()
  
  // Get real-time P2P statistics
  const messageStats = connectionManager?.getMessageService().getStats()
  const connectionCount = connections.size
  const messageCount = messageStats?.totalMessages || 0
  const networkStatus = isNetworkActive ? 'online' : 'offline'
  
  // Calculate uptime from when network was initialized
  const uptime = currentSelfta?.createdAt
    ? (() => {
        try {
          // Ensure createdAt is a Date object
          const createdAtDate = currentSelfta.createdAt instanceof Date
            ? currentSelfta.createdAt
            : new Date(currentSelfta.createdAt)

          const timeDiff = currentTime.getTime() - createdAtDate.getTime()
          return new Date(timeDiff).toISOString().substr(11, 8)
        } catch (error) {
          console.error('Error calculating uptime:', error)
          return '00:00:00'
        }
      })()
    : '00:00:00'

  useEffect(() => {
    // Update current time for uptime calculation
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const statCards = [
    {
      title: 'Active Connections',
      value: connectionCount,
      icon: Users,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50',
    },
    {
      title: 'Messages Sent',
      value: messageCount,
      icon: MessageCircle,
      color: 'text-success-600',
      bgColor: 'bg-success-50',
    },
    {
      title: 'Network Status',
      value: networkStatus === 'online' ? 'Online' : 'Offline',
      icon: Wifi,
      color: networkStatus === 'online' ? 'text-success-600' : 'text-error-600',
      bgColor: networkStatus === 'online' ? 'bg-success-50' : 'bg-error-50',
    },
    {
      title: 'Session Time',
      value: uptime,
      icon: User,
      color: 'text-secondary-600',
      bgColor: 'bg-secondary-50',
    },
  ]

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-full overflow-hidden">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-2 break-words">
          Welcome to useSelft
        </h1>
        <p className="text-sm sm:text-base text-secondary-600 break-words">
          Your decentralized peer-to-peer communication platform
        </p>
      </div>

      {/* Stats Grid - Mobile-First Responsive Design */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statCards.map((stat) => {
          const IconComponent = stat.icon
          return (
            <div key={stat.title} className="card dashboard-stat-card">
              <div className="card-content">
                <div className="dashboard-stat-content">
                  <div className="dashboard-stat-text">
                    <p className="text-xs sm:text-sm font-medium text-secondary-600 mb-1 break-words leading-tight">
                      {stat.title}
                    </p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-secondary-900 break-words leading-tight">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`dashboard-stat-icon p-2 sm:p-3 rounded-lg ${stat.bgColor}`}>
                    <IconComponent className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.color}`} />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Quick Start</h3>
            <p className="card-description">
              Get started with your first connection
            </p>
          </div>
          <div className="card-content space-y-4">
            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <div className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium mr-3">
                  1
                </div>
                <span>Create your avatar and profile</span>
              </div>
              <div className="flex items-center text-sm">
                <div className="w-6 h-6 bg-secondary-100 text-secondary-600 rounded-full flex items-center justify-center text-xs font-medium mr-3">
                  2
                </div>
                <span>Connect to other peers</span>
              </div>
              <div className="flex items-center text-sm">
                <div className="w-6 h-6 bg-secondary-100 text-secondary-600 rounded-full flex items-center justify-center text-xs font-medium mr-3">
                  3
                </div>
                <span>Start messaging and sharing</span>
              </div>
            </div>
          </div>
          <div className="card-footer">
            <button className="btn btn-primary w-full">
              Get Started
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Activity</h3>
            <p className="card-description">
              Your latest connections and messages
            </p>
          </div>
          <div className="card-content">
            {connectionCount === 0 ? (
              <div className="text-center text-secondary-500 py-8">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No recent activity</p>
                <p className="text-xs">Activity will appear here once you start connecting</p>
              </div>
            ) : (
              <div className="space-y-3">
                {Array.from(connections.values()).slice(0, 5).map((conn) => (
                  <div key={conn.peerId} className="flex items-center space-x-3 py-2">
                    <div className="w-8 h-8 bg-secondary-200 rounded-full flex items-center justify-center overflow-hidden">
                      {conn.selfta?.profileImage ? (
                        <img
                          src={conn.selfta.profileImage}
                          alt={`${conn.selfta.displayName || conn.selfta.alias}'s avatar`}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <span className="text-secondary-600 text-sm font-medium">
                          {/* CRITICAL FIX: Proper alias resolution for avatar initial */}
                          {(conn.selfta?.alias || conn.alias || `user_${conn.id?.slice(-8)}`).charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary-900">
                        {/* CRITICAL FIX: Proper alias resolution for display name */}
                        {conn.selfta?.displayName || conn.displayName || conn.selfta?.alias || conn.alias || `User_${conn.id?.slice(-8) || 'Unknown'}`}
                      </p>
                      <p className="text-xs text-secondary-500">
                        Connected {(() => {
                          try {
                            if (!conn.connectedAt) return 'Unknown time'
                            const connectedDate = conn.connectedAt instanceof Date
                              ? conn.connectedAt
                              : new Date(conn.connectedAt)
                            return connectedDate.toLocaleTimeString()
                          } catch {
                            return 'Unknown time'
                          }
                        })()}
                      </p>
                    </div>
                    <div className={`w-2 h-2 rounded-full ${
                      conn.isConnected ? 'bg-success-500' : 'bg-secondary-400'
                    }`} />
                  </div>
                ))}
                {connectionCount > 5 && (
                  <p className="text-xs text-secondary-500 text-center pt-2">
                    +{connectionCount - 5} more connections
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}